import { useState, useEffect } from 'react'
import { useBarcode } from '../../../../hooks/useBarcode'
import { useFragment } from '../../../../gql'
import { OrderReturnsTable_OrderItemFragment } from '../../components/OrderReturnsTable'
import { BarcodeScanner } from '../../../../services/BarcodeScanner'

interface BarcodeModalItem {
  item: any
  quantityReturned: number
}

interface UseReturnBarcodeProps {
  orderItemsAvailableToReturn: any[]
  enabled?: boolean
}

export const useReturnBarcode = ({ orderItemsAvailableToReturn, enabled = true }: UseReturnBarcodeProps) => {
  const [barcodeModalItem, setBarcodeModalItem] = useState<BarcodeModalItem | null>(null)

  // Barcode scan logic for opening modal for a single item
  useBarcode(
    event => {
      if (!enabled || barcodeModalItem) return // Only one at a time

      if (event.type === 'BARCODE') {
        // Ignore box/package barcodes; those are handled elsewhere
        if (BarcodeScanner.isBoxBarcode(event.data)) return

        // Find matching item by barcode
        const match = orderItemsAvailableToReturn.find(item => {
          const frag = useFragment(OrderReturnsTable_OrderItemFragment, item)
          const codes = [frag.code, frag.ean]

          if (frag.product && typeof frag.product === 'object') {
            if ('code' in frag.product && frag.product.code) {
              codes.push(String(frag.product.code))
            }
            for (const field of ['ean', 'ean2', 'ean3', 'ean4', 'ean5'] as const) {
              if (field in frag.product && frag.product[field]) {
                codes.push(String(frag.product[field]))
              }
            }
          }

          return codes.filter(Boolean).includes(event.data)
        })

        if (match) {
          setBarcodeModalItem({
            item: match,
            quantityReturned: match.quantityReturned,
          })
        }
      }
    },
    { autoStart: enabled },
  )

  const clearBarcodeItem = () => {
    setBarcodeModalItem(null)
  }

  return {
    barcodeModalItem,
    clearBarcodeItem,
  }
}
